import React, { useState, useRef, useEffect } from "react";
import exifr from "exifr";
import QRCode from "qrcode";

function App() {
  const [imageData, setImageData] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [link, setLink] = useState("");
  const [exifData, setExifData] = useState(null);
  const [lensModel, setLensModel] = useState("");
  const canvasRef = useRef(null);
  const dropZoneRef = useRef(null);

  const [debouncedTitle, setDebouncedTitle] = useState("");
  const [debouncedAuthor, setDebouncedAuthor] = useState("");
  const [debouncedLink, setDebouncedLink] = useState("");

  const debounceDelay = 500; // Retraso de 500ms para el debounce

  const maxWidth = 1200;
  const maxHeight = 900;
  const minWidth = 600;
  const minHeight = 400;

  useEffect(() => {
    const handler = setTimeout(() => {
      console.log(
        `Title: ${debouncedTitle}, Author: ${debouncedAuthor}, Link: ${debouncedLink}`
      );
    }, debounceDelay);

    return () => clearTimeout(handler);
  }, [debouncedTitle, debouncedAuthor, debouncedLink]);

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setDebouncedTitle(e.target.value); // Actualiza el valor debounced
  };

  const handleAuthorChange = (e) => {
    setAuthor(e.target.value);
    setDebouncedAuthor(e.target.value); // Actualiza el valor debounced
  };

  const handleLinkChange = (e) => {
    setLink(e.target.value);
    setDebouncedLink(e.target.value); // Actualiza el valor debounced
  };

  const handleFileChange = (event) => {
    const file = event.target.files
      ? event.target.files[0]
      : event.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const image = new Image();
        const binaryString = e.target.result;

        image.src = binaryString;
        image.onload = async () => {
          const originalWidth = image.width;
          const originalHeight = image.height;

          let canvasWidth = originalWidth;
          let canvasHeight = originalHeight;

          if (canvasWidth > maxWidth) {
            canvasWidth = maxWidth;
            canvasHeight = (maxWidth / originalWidth) * originalHeight;
          }

          if (canvasHeight > maxHeight) {
            canvasHeight = maxHeight;
            canvasWidth = (maxHeight / originalHeight) * originalWidth;
          }

          if (canvasWidth < minWidth) {
            canvasWidth = minWidth;
            canvasHeight = (minWidth / originalWidth) * originalHeight;
          }

          if (canvasHeight < minHeight) {
            canvasHeight = minHeight;
            canvasWidth = (minHeight / originalHeight) * originalWidth;
          }

          const padding = 40;
          const canvas = canvasRef.current;
          canvas.width = canvasWidth + padding * 2;
          canvas.height = canvasHeight + padding * 4;

          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          ctx.drawImage(image, padding, padding, canvasWidth, canvasHeight);

          try {
            const exifData = await exifr.parse(file);
            const lens = exifData.LensModel || "";
            const camera = exifData.Model || "";
            const make = exifData.Make || "";
            const aperture = exifData.FNumber || "";
            const iso = exifData.ISO || "";
            const exposureTime = exifData.ExposureTime
              ? decimalToFraction(exifData.ExposureTime)
              : "";

            setLensModel(lens);
            setExifData(exifData);

            const cameraDisplay = camera.includes(make)
              ? camera
              : `${make} ${camera}`;

            drawCanvas(
              canvas,
              ctx,
              image,
              canvasWidth,
              canvasHeight,
              padding,
              title,
              author,
              link,
              cameraDisplay,
              lens,
              aperture,
              iso,
              exposureTime
            );

            setImageData(image);
          } catch (error) {
            console.error("Error al leer los metadatos EXIF:", error);
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const drawCanvas = (
    canvas,
    ctx,
    image,
    canvasWidth,
    canvasHeight,
    padding,
    title,
    author,
    link,
    cameraDisplay,
    lens,
    aperture,
    iso,
    exposureTime
  ) => {
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(image, padding, padding, canvasWidth, canvasHeight);

    ctx.font = "14px Arial";
    ctx.fillStyle = "black";
    ctx.textAlign = "left";

    const titleText = title ? `"${title}"` : "";
    ctx.fillText(`${author}`, padding, canvasHeight + padding * 1.55);
    ctx.fillText(titleText, padding, canvasHeight + padding * 2);

    if (link) {
      const qrSize = 100;
      const qrCanvas = document.createElement("canvas");
      QRCode.toCanvas(
        qrCanvas,
        link,
        { width: qrSize, margin: 2 },
        function (error) {
          if (error) console.error(error);

          ctx.drawImage(
            qrCanvas,
            canvas.width - qrSize - padding,
            canvasHeight + padding
          );
          ctx.textAlign = "right";
          const metadataY = canvasHeight + padding * 1.55;
          let lineOffset = 0;
          if (cameraDisplay) {
            ctx.fillText(
              cameraDisplay,
              canvas.width - qrSize - padding * 1.2,
              metadataY + lineOffset
            );
            lineOffset += 20;
          }
          if (lens) {
            ctx.fillText(
              `${lens}`,
              canvas.width - qrSize - padding * 1.2,
              metadataY + lineOffset
            );
            lineOffset += 20;
          }
          if (aperture || exposureTime || iso) {
            ctx.fillText(
              `f/${aperture} ${exposureTime} ${iso}`,
              canvas.width - qrSize - padding * 1.2,
              metadataY + lineOffset
            );
          }
        }
      );
    } else {
      const qrSize = 0;
      ctx.textAlign = "right";
      const metadataY = canvasHeight + padding * 1.55;
      let lineOffset = 0;
      if (cameraDisplay) {
        ctx.fillText(
          cameraDisplay,
          canvas.width - qrSize - padding * 1.2,
          metadataY + lineOffset
        );
        lineOffset += 20;
      }
      if (lens) {
        ctx.fillText(
          `${lens}`,
          canvas.width - qrSize - padding * 1.2,
          metadataY + lineOffset
        );
        lineOffset += 20;
      }
      if (aperture || exposureTime || iso) {
        ctx.fillText(
          `f/${aperture} ${exposureTime} ${iso}`,
          canvas.width - qrSize - padding * 1.2,
          metadataY + lineOffset
        );
      }
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "inpho.png";
    link.href = canvas.toDataURL();
    link.click();
  };

  const decimalToFraction = (decimal) => {
    const tolerance = 1e-6;
    let denominator = 1;
    let numerator = decimal;

    while (Math.abs(numerator - Math.round(numerator)) > tolerance) {
      denominator *= 10;
      numerator = decimal * denominator;
    }

    const gcd = (a, b) => {
      if (b === 0) return a;
      return gcd(b, a % b);
    };
    const greatestCommonDivisor = gcd(Math.round(numerator), denominator);

    numerator = Math.round(numerator / greatestCommonDivisor);
    denominator = Math.round(denominator / greatestCommonDivisor);

    const maxDigits = 5;
    const maxValue = Math.pow(10, maxDigits) - 1;

    if (numerator > maxValue || denominator > maxValue) {
      const scaleFactor = Math.max(
        Math.ceil(numerator / maxValue),
        Math.ceil(denominator / maxValue)
      );
      numerator = Math.round(numerator / scaleFactor);
      denominator = Math.round(denominator / scaleFactor);
    }

    const formatNumber = (num) => num.toLocaleString("de-DE");

    return `${formatNumber(numerator)}/${formatNumber(denominator)}`;
  };

  useEffect(() => {
    if (imageData) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Procesamos los metadatos EXIF para obtener el nombre de la cámara y lente
      const lens = exifData?.LensModel || "";
      const camera = exifData?.Model || "";
      const make = exifData?.Make || "";
      const aperture = exifData?.FNumber || "";
      const iso = exifData?.ISO || "";
      const exposureTime = exifData?.ExposureTime
        ? decimalToFraction(exifData.ExposureTime)
        : "";

      // Aquí combinamos el modelo de la cámara y la marca de la cámara
      const cameraDisplay = camera.includes(make)
        ? camera
        : `${make} ${camera}`;

      // Redibujamos el canvas con los nuevos datos
      drawCanvas(
        canvas,
        ctx,
        imageData,
        canvas.width - 80, // Ancho ajustado para el padding
        canvas.height - 160, // Alto ajustado para el padding
        40, // padding
        debouncedTitle,
        debouncedAuthor,
        debouncedLink,
        cameraDisplay, // Aquí pasamos el nombre de la cámara con la marca
        lens,
        aperture,
        iso,
        exposureTime
      );
    }
  }, [debouncedTitle, debouncedAuthor, debouncedLink, imageData, exifData]);

  return (
    <div className="container">
      <div>
        <header
          className="app-header"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "1rem",
            backgroundColor: "black",
            borderBottom: "1px solid #ddd",
            fontFamily: "sans-serif",
            marginBottom: "1rem",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: "2rem",
              fontWeight: "bold",
              color: "white",
            }}
          >
            inpho
          </h1>
          <span style={{ fontSize: "0.9rem", color: "#555" }}>v1.0</span>
          <span
            style={{ fontSize: "1rem", color: "#777", marginTop: "0.5rem" }}
          >
            Metadata e incorporación de QR
          </span>
        </header>

        <div
          ref={dropZoneRef}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFileChange(e);
          }}
          onClick={() => document.getElementById("fileInput").click()}
          className="drop-zone"
        >
          <p>
            Arrastra y suelta una imagen aquí o haz clic para seleccionar una
            imagen
          </p>
          <input
            id="fileInput"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>

        <input
          type="text"
          placeholder="Autor de la imagen"
          value={author}
          onChange={handleAuthorChange}
          className="input"
        />
        <input
          type="text"
          placeholder="Título de la imagen"
          value={title}
          onChange={handleTitleChange}
          className="input"
        />
        <input
          type="url"
          placeholder="Enlace (opcional)"
          value={link}
          onChange={handleLinkChange}
          className="input"
        />
        {imageData && (
          <button onClick={handleDownload} className="download-btn">
            Descargar Imagen
          </button>
        )}
      </div>
      <div>
        <div className="canvas-container">
          <canvas ref={canvasRef}></canvas>
        </div>
      </div>
    </div>
  );
}

export default App;

/* CSS Styles in the same file */
const styles = `
  body {
    font-family: 'Arial', sans-serif;
    margin: 0;
    padding: 0;
    background-color: #f9fafb;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
  }

  .container {
    text-align: center;
    padding: 40px;
    max-width: 90%;
    width: 90vw;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    display:flex;
    justify-content: space-around;
    gap: 30px;
    align-items: center;
    min-height: 84vh;
    margin: 0 auto;
  }
    @media (max-width: 768px) {
  .container {
    flex-direction: column;
  }
}

  .input {
    width: 100%;
    padding: 12px 16px;
    margin: 10px 0;
    border: 2px solid #ddd;
    //border-radius: 8px;
    font-size: 16px;
    box-sizing: border-box;
    transition: border-color 0.3s ease;
  }

  .input:focus {
    outline: none;
    border-color: black;
  }

  .drop-zone {
    width: 100%%;
    min-height: 50px;
    border: 2px dashed #ccc;
    padding: 20px;
    text-align: center;
    margin-bottom: 10px;
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.3s ease, border-color 0.3s ease;
  }

  .drop-zone:hover {
    background-color: #f0f4f8;
    border-color: black;
  }

  .drop-zone p {
    font-size: 16px;
    color: #555;
  }

  canvas {
    width: 100%;
    height: auto;
    margin-top: 20px;
    //background-color: #f3f4f6;
    border-radius: 8px;
    max-height: 100vh;
    max-width: 100%;
  }

  .download-btn {
    margin-top: 20px;
    padding: 12px 24px;
    background-color: black;
    color: white;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s ease;
    width: 100%;
  }

  .download-btn:hover {
    background-color: #444;
  }

  .download-btn:focus {
    outline: none;
  }

  .canvas-container {
    width: 100%;
    text-align: center;
  }
`;

// Insert styles dynamically
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
